<script setup>
import { ref, computed, onMounted, onBeforeUnmount, watch } from 'vue';
import { useStore } from 'vuex';
import { useRouter } from 'vue-router';
import { useAlert } from 'dashboard/composables';
import { useFunnelManager } from 'dashboard/composables/kanban/useFunnelManager';
import Button from 'dashboard/components-next/button/Button.vue';
import FollowUpMessageEditor from './components/kanban/FollowUpMessageEditor.vue';

const store = useStore();
const router = useRouter();
const { funnels, currentFunnel, loadFunnels, selectFunnel, updateFunnel } =
  useFunnelManager();

// Função para voltar ao Kanban
const goBackToKanban = () => {
  router.push({ name: 'kanban_view' });
};

// Estado local
const autoMoveEnabled = ref(false);
const notifyOnStageChange = ref(true);
const allowManualMove = ref(true);
const enableSLA = ref(false);
const enableAutomation = ref(false);
const activeTab = ref('basic');
const selectedAgents = ref([]);

// Follow-up messages state
const showFollowUpEditor = ref(false);
const selectedStageForFollowUp = ref(null);

// SLA state
const stageSlaHours = ref({});

// Computed
const availableAgents = computed(() => {
  return store.getters['agents/getAgents'] || [];
});

// Ordered stages for follow-up section
const orderedStages = computed(() => {
  if (!currentFunnel.value?.stages) return [];
  return Object.entries(currentFunnel.value.stages)
    .sort((a, b) => (a[1].position || 0) - (b[1].position || 0))
    .map(([name, config]) => ({
      name,
      label: config.label || name,
      color: config.color || '#6366f1',
      followUpCount: (config.follow_up_messages || []).filter(m => m.enabled).length,
      totalCount: (config.follow_up_messages || []).length,
    }));
});

// Ordered stages for SLA section
const orderedStagesForSla = computed(() => {
  if (!currentFunnel.value?.stages) return [];
  return Object.entries(currentFunnel.value.stages)
    .sort((a, b) => (a[1].position || 0) - (b[1].position || 0))
    .map(([name, config]) => ({
      name,
      label: config.label || name,
      color: config.color || '#6366f1',
      slaHours: config.sla_hours || 0,
    }));
});

// Open follow-up editor for a stage
const openFollowUpEditor = stage => {
  selectedStageForFollowUp.value = stage;
  showFollowUpEditor.value = true;
};

// Close follow-up editor
const closeFollowUpEditor = () => {
  showFollowUpEditor.value = false;
  selectedStageForFollowUp.value = null;
};

// Handle follow-up saved - reload funnel to get updated data
const handleFollowUpSaved = async () => {
  // Reload funnel data to get updated follow_up_messages
  if (currentFunnel.value?.id) {
    try {
      await store.dispatch('funnels/show', currentFunnel.value.id);
      await loadFunnels(true);
    } catch {
      // Silently fail
    }
  }
};

// Helper para aguardar funil estar disponível
const waitForCurrentFunnel = async (maxAttempts = 10) => {
  let attempts = 0;
  while (!currentFunnel.value && attempts < maxAttempts) {
    // Waiting for funnel
    await new Promise(resolve => setTimeout(resolve, 100));
    attempts++;
  }
  return currentFunnel.value;
};

// Methods
const loadSettings = async () => {
  const funnel = await waitForCurrentFunnel();

  if (!funnel) {
    // Timeout: No funnel available
    return;
  }

  // Loading settings

  // Resetar todos os valores primeiro
  autoMoveEnabled.value = false;
  notifyOnStageChange.value = true;
  allowManualMove.value = true;
  enableSLA.value = false;
  enableAutomation.value = false;
  stageSlaHours.value = {};

  // Carregar SLA hours dos stages
  if (funnel.stages) {
    Object.entries(funnel.stages).forEach(([name, config]) => {
      if (config.sla_hours) {
        stageSlaHours.value[name] = config.sla_hours;
      }
    });
  }

  try {
    // Se o funil for local (sem ID real), usar dados locais
    if (
      typeof currentFunnel.value.id === 'string' &&
      currentFunnel.value.id.startsWith('default-')
    ) {
      // Carregar configurações do funil (já deve ter sido carregado do localStorage pelo composable)
      if (currentFunnel.value.settings) {
        const settings = currentFunnel.value.settings;

        autoMoveEnabled.value = settings.auto_move || false;
        notifyOnStageChange.value = settings.notify_on_stage_change !== false;
        allowManualMove.value = settings.allow_manual_move !== false;
        enableSLA.value = settings.enable_sla || false;
        enableAutomation.value = settings.enable_automation || false;
        selectedAgents.value = settings.agent_ids || [];
      }
      return;
    }

    // Para funis reais, buscar dados no backend
    const response = await store.dispatch(
      'funnels/show',
      currentFunnel.value.id
    );
    const funnelData = response || currentFunnel.value;

    // Carregar configurações específicas do funil
    if (funnelData?.settings) {
      const settings = funnelData.settings;
      autoMoveEnabled.value = settings.auto_move || false;
      notifyOnStageChange.value = settings.notify_on_stage_change !== false;
      allowManualMove.value = settings.allow_manual_move !== false;
      enableSLA.value = settings.enable_sla || false;
      enableAutomation.value = settings.enable_automation || false;
      selectedAgents.value = settings.agent_ids || [];
    }
  } catch (error) {
    // Ignorar erros de cancelamento (navegação)
    if (error?.name === 'CanceledError' || error?.code === 'ERR_CANCELED') {
      return;
    }
    // Fallback para dados locais se der erro
    if (currentFunnel.value?.settings) {
      const settings = currentFunnel.value.settings;
      autoMoveEnabled.value = settings.auto_move || false;
      notifyOnStageChange.value = settings.notify_on_stage_change !== false;
      allowManualMove.value = settings.allow_manual_move !== false;
      enableSLA.value = settings.enable_sla || false;
      enableAutomation.value = settings.enable_automation || false;
      selectedAgents.value = settings.agent_ids || [];
    }
  }
};

const saveSettings = async () => {
  if (!currentFunnel.value) {
    console.error('Nenhum funil selecionado para salvar');
    return;
  }

  try {
    const updatedSettings = {
      ...currentFunnel.value.settings,
      auto_move: autoMoveEnabled.value,
      notify_on_stage_change: notifyOnStageChange.value,
      allow_manual_move: allowManualMove.value,
      enable_sla: enableSLA.value,
      enable_automation: enableAutomation.value,
      agent_ids: selectedAgents.value,
      updated_at: new Date().toISOString(),
    };

    // Atualizar sla_hours nos stages
    const updatedStages = { ...currentFunnel.value.stages };
    Object.keys(stageSlaHours.value).forEach(stageName => {
      if (updatedStages[stageName]) {
        updatedStages[stageName] = {
          ...updatedStages[stageName],
          sla_hours: stageSlaHours.value[stageName] || 0,
        };
      }
    });

    // Se for funil local, usar updateFunnel que salvará no localStorage
    if (
      typeof currentFunnel.value.id === 'string' &&
      currentFunnel.value.id.startsWith('default-')
    ) {
      await updateFunnel(currentFunnel.value.id, {
        settings: updatedSettings,
        stages: updatedStages,
      });

      // Atualizar o funil local com as novas configurações
      currentFunnel.value.settings = updatedSettings;
      currentFunnel.value.stages = updatedStages;

      // Atualizar na lista de funis também
      const funnelIndex = funnels.value.findIndex(
        f => f.id === currentFunnel.value.id
      );
      if (funnelIndex !== -1) {
        funnels.value[funnelIndex].settings = updatedSettings;
        funnels.value[funnelIndex].stages = updatedStages;
      }

      useAlert('Configurações salvas localmente com sucesso!');

      // Emitir evento para outros componentes
      window.dispatchEvent(
        new CustomEvent('funnel-settings-saved', {
          detail: {
            funnelId: currentFunnel.value.id,
            settings: updatedSettings,
          },
        })
      );

      return;
    }

    // Para funis reais, salvar no backend
    await updateFunnel(currentFunnel.value.id, {
      settings: updatedSettings,
      stages: updatedStages,
    });

    // Atualizar o funil local com as novas configurações
    currentFunnel.value.settings = updatedSettings;
    currentFunnel.value.stages = updatedStages;

    // Atualizar na lista de funis também
    const funnelIndex = funnels.value.findIndex(
      f => f.id === currentFunnel.value.id
    );
    if (funnelIndex !== -1) {
      funnels.value[funnelIndex].settings = updatedSettings;
      funnels.value[funnelIndex].stages = updatedStages;
    }

    useAlert(
      'Configurações salvas com sucesso! As conversas serão roteadas conforme configurado.'
    );

    // Emitir evento para outros componentes saberem que as configurações mudaram
    window.dispatchEvent(
      new CustomEvent('funnel-settings-saved', {
        detail: {
          funnelId: currentFunnel.value.id,
          settings: updatedSettings,
        },
      })
    );
  } catch (error) {
    console.error('Erro ao salvar configurações:', error);
    useAlert('Erro ao salvar configurações. Tente novamente.');
  }
};

// Event listeners
const handleFunnelChange = event => {
  // Só recarregar se realmente mudou o funil
  if (
    event.detail.newFunnelId &&
    event.detail.newFunnelId != event.detail.oldFunnelId
  ) {
    window._lastFunnelChangeTime = Date.now();
    setTimeout(() => {
      loadSettings();
    }, 50);
  }
};

// Track if component is mounted
const isMounted = ref(false);

// Lifecycle
onMounted(async () => {
  isMounted.value = true;

  // Carregar agentes
  await store.dispatch('agents/get');

  await loadFunnels();

  if (funnels.value.length > 0 && !currentFunnel.value) {
    await selectFunnel(funnels.value[0].id);
  }

  // Só carregar settings após ter funil disponível
  if (currentFunnel.value) {
    loadSettings();
  }

  // Adicionar listener para mudanças de funil
  window.addEventListener('funnel-changed', handleFunnelChange);
});

// Cleanup
onBeforeUnmount(() => {
  isMounted.value = false;
  window.removeEventListener('funnel-changed', handleFunnelChange);
});

// Watch para recarregar configurações quando trocar de funil
// Bug #32 Fix: Watch only the ID to prevent loops from deep object changes
watch(
  () => currentFunnel.value?.id,
  (newFunnelId, oldFunnelId) => {
    // Skip if component not mounted or no funnel
    if (!isMounted.value || !newFunnelId) return;

    // Only reload if ID actually changed
    if (newFunnelId !== oldFunnelId) {
      // Só carregar se não foi carregado recentemente pelo evento
      const recentlyLoaded =
        window._lastFunnelChangeTime &&
        Date.now() - window._lastFunnelChangeTime < 500;

      if (!recentlyLoaded) {
        loadSettings();
        window._lastFunnelChangeTime = Date.now();
      }
    }
  },
  { immediate: false }
);
</script>

<template>
  <div class="flex flex-col h-full w-full bg-n-background">
    <!-- Header -->
    <div class="flex items-center justify-between px-6 py-5 border-b border-n-weak">
      <div class="flex items-center gap-4">
        <button
          class="size-9 rounded-lg flex items-center justify-center text-n-slate-11 hover:text-n-slate-12 hover:bg-n-alpha-2 transition-colors"
          title="Voltar para o Kanban"
          @click="goBackToKanban"
        >
          <span class="i-lucide-arrow-left size-5" />
        </button>
        <div>
          <h1 class="text-lg font-medium text-n-slate-12">
            Configurações e Permissões
          </h1>
          <p class="text-sm text-n-slate-11 mt-0.5">
            Configure comportamentos, permissões e automações do funil
          </p>
        </div>
      </div>

      <!-- Seletor de Funil -->
      <div v-if="funnels.length > 0" class="flex items-center gap-3">
        <label class="text-sm font-medium text-n-slate-11">Funil:</label>
        <select
          :value="currentFunnel?.id"
          class="funnel-select h-9 w-[180px] pl-3 pr-8 rounded-lg border border-n-weak bg-n-solid-2 text-n-slate-12 text-sm focus:border-woot-500 focus:outline-none transition-colors mb-0 appearance-none bg-no-repeat"
          @change="e => selectFunnel(e.target.value)"
        >
          <option
            v-for="funnel in funnels"
            :key="funnel.id"
            :value="funnel.id"
          >
            {{ funnel.settings?.icon || '📊' }} {{ funnel.name }}
          </option>
        </select>
      </div>
    </div>

    <!-- Conteúdo Principal -->
    <div v-if="currentFunnel" class="flex-1 overflow-y-auto p-6">
      <div class="max-w-5xl mx-auto space-y-6">
        <!-- Card: Comportamentos -->
        <div class="rounded-xl bg-n-solid-2 outline outline-1 outline-n-container p-6">
          <div class="flex items-center gap-2 mb-4">
            <span class="i-lucide-settings-2 size-5 text-n-slate-11" />
            <h2 class="text-base font-medium text-n-slate-12">Comportamentos</h2>
          </div>

          <div class="space-y-3">
            <label class="flex items-center gap-3 p-3 rounded-lg hover:bg-n-alpha-1 cursor-pointer transition-colors">
              <input
                v-model="autoMoveEnabled"
                type="checkbox"
                class="size-4 rounded border-n-weak text-woot-500 focus:ring-woot-500"
              />
              <div class="flex-1">
                <span class="text-sm font-medium text-n-slate-12">
                  Mover automaticamente entre etapas
                </span>
                <p class="text-xs text-n-slate-10 mt-0.5">
                  Conversas serão movidas automaticamente baseado em regras
                </p>
              </div>
            </label>

            <label class="flex items-center gap-3 p-3 rounded-lg hover:bg-n-alpha-1 cursor-pointer transition-colors">
              <input
                v-model="notifyOnStageChange"
                type="checkbox"
                class="size-4 rounded border-n-weak text-woot-500 focus:ring-woot-500"
              />
              <div class="flex-1">
                <span class="text-sm font-medium text-n-slate-12">
                  Notificar agente sobre mudanças de etapa
                </span>
                <p class="text-xs text-n-slate-10 mt-0.5">
                  Agentes receberão notificação quando conversas mudarem de etapa
                </p>
              </div>
            </label>

            <label class="flex items-center gap-3 p-3 rounded-lg hover:bg-n-alpha-1 cursor-pointer transition-colors">
              <input
                v-model="allowManualMove"
                type="checkbox"
                class="size-4 rounded border-n-weak text-woot-500 focus:ring-woot-500"
              />
              <div class="flex-1">
                <span class="text-sm font-medium text-n-slate-12">
                  Permitir movimentação manual
                </span>
                <p class="text-xs text-n-slate-10 mt-0.5">
                  Agentes podem arrastar conversas entre etapas no Kanban
                </p>
              </div>
            </label>
          </div>
        </div>

        <!-- Card: Permissões de Acesso -->
        <div class="rounded-xl bg-n-solid-2 outline outline-1 outline-n-container p-6">
          <div class="flex items-center gap-2 mb-4">
            <span class="i-lucide-users size-5 text-n-slate-11" />
            <h2 class="text-base font-medium text-n-slate-12">Agentes com Acesso</h2>
          </div>

          <p class="text-sm text-n-slate-11 mb-4">
            Selecione os agentes que podem ver e trabalhar com este funil. 
            Se nenhum for selecionado, todos os agentes terão acesso.
          </p>

          <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2 max-h-64 overflow-y-auto p-1">
            <label
              v-for="agent in availableAgents"
              :key="agent.id"
              class="flex items-center gap-3 p-3 rounded-lg hover:bg-n-alpha-1 cursor-pointer transition-colors"
              :class="selectedAgents.includes(agent.id) ? 'bg-woot-500/10 outline outline-1 outline-woot-500/30' : ''"
            >
              <input
                v-model="selectedAgents"
                type="checkbox"
                :value="agent.id"
                class="size-4 rounded border-n-weak text-woot-500 focus:ring-woot-500"
              />
              <div class="flex items-center gap-2 flex-1 min-w-0">
                <img
                  v-if="agent.avatar_url"
                  :src="agent.avatar_url"
                  :alt="agent.name"
                  class="size-8 rounded-full object-cover"
                />
                <div
                  v-else
                  class="size-8 rounded-full bg-woot-500 flex items-center justify-center text-white text-sm font-medium flex-shrink-0"
                >
                  {{ agent.name.charAt(0).toUpperCase() }}
                </div>
                <span class="text-sm text-n-slate-12 truncate">
                  {{ agent.name }}
                </span>
              </div>
            </label>
          </div>

          <div
            v-if="selectedAgents.length > 0"
            class="mt-4 px-3 py-2 rounded-lg bg-n-teal-3 text-n-teal-11 text-sm"
          >
            <span class="i-lucide-check-circle size-4 inline-block mr-1.5 align-text-bottom" />
            {{ selectedAgents.length }} agente(s) com acesso a este funil
          </div>
        </div>

        <!-- Card: SLA e Automações -->
        <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
          <!-- SLA -->
          <div class="rounded-xl bg-n-solid-2 outline outline-1 outline-n-container p-6">
            <div class="flex items-center gap-2 mb-4">
              <span class="i-lucide-clock size-5 text-n-slate-11" />
              <h2 class="text-base font-medium text-n-slate-12">Controle de SLA</h2>
            </div>

            <label class="flex items-center gap-3 p-3 rounded-lg hover:bg-n-alpha-1 cursor-pointer transition-colors">
              <input
                v-model="enableSLA"
                type="checkbox"
                class="size-4 rounded border-n-weak text-woot-500 focus:ring-woot-500"
              />
              <div class="flex-1">
                <span class="text-sm font-medium text-n-slate-12">
                  Ativar controle de SLA
                </span>
                <p class="text-xs text-n-slate-10 mt-0.5">
                  Monitora tempo em cada etapa e alerta sobre violações
                </p>
              </div>
            </label>

            <!-- SLA por etapa -->
            <div v-if="enableSLA" class="mt-4 space-y-2">
              <p class="text-xs text-n-slate-10 mb-2">
                Defina o tempo máximo (em horas) que uma conversa pode ficar em cada etapa:
              </p>
              <div
                v-for="stage in orderedStagesForSla"
                :key="stage.name"
                class="flex items-center gap-3 p-2 rounded-lg bg-n-alpha-1"
              >
                <div
                  class="w-1 h-6 rounded-full flex-shrink-0"
                  :style="{ backgroundColor: stage.color }"
                />
                <span class="text-sm text-n-slate-12 flex-1 truncate">
                  {{ stage.label }}
                </span>
                <input
                  v-model.number="stageSlaHours[stage.name]"
                  type="number"
                  min="0"
                  max="720"
                  placeholder="0"
                  class="w-20 h-8 px-2 text-sm text-center bg-n-solid-3 border border-n-weak rounded-md text-n-slate-12 focus:outline-none focus:border-woot-500"
                />
                <span class="text-xs text-n-slate-10">horas</span>
              </div>
            </div>
          </div>

          <!-- Automações -->
          <div class="rounded-xl bg-n-solid-2 outline outline-1 outline-n-container p-6">
            <div class="flex items-center gap-2 mb-4">
              <span class="i-lucide-workflow size-5 text-n-slate-11" />
              <h2 class="text-base font-medium text-n-slate-12">Automações</h2>
            </div>

            <label class="flex items-center gap-3 p-3 rounded-lg hover:bg-n-alpha-1 cursor-pointer transition-colors">
              <input
                v-model="enableAutomation"
                type="checkbox"
                class="size-4 rounded border-n-weak text-woot-500 focus:ring-woot-500"
              />
              <div class="flex-1">
                <span class="text-sm font-medium text-n-slate-12">
                  Habilitar automações
                </span>
                <p class="text-xs text-n-slate-10 mt-0.5">
                  Permite regras automáticas para movimentação entre etapas
                </p>
              </div>
            </label>

            <!-- Link para regras de automação -->
            <div v-if="enableAutomation" class="mt-4 p-3 rounded-lg bg-n-alpha-1">
              <p class="text-xs text-n-slate-10 mb-2">
                Configure regras de automação para mover conversas automaticamente.
              </p>
              <router-link
                :to="{ name: 'automation_list' }"
                class="inline-flex items-center gap-1 text-sm text-woot-500 hover:text-woot-600"
              >
                <span class="i-lucide-external-link size-4" />
                Ir para Automações
              </router-link>
            </div>
          </div>
        </div>

        <!-- Card: Mensagens de Follow-up -->
        <div class="rounded-xl bg-n-solid-2 outline outline-1 outline-n-container p-6">
          <div class="flex items-center gap-2 mb-4">
            <span class="i-lucide-message-square-plus size-5 text-n-slate-11" />
            <h2 class="text-base font-medium text-n-slate-12">Mensagens de Follow-up</h2>
          </div>

          <p class="text-sm text-n-slate-11 mb-4">
            Configure mensagens automáticas que serão enviadas quando uma conversa entrar em cada etapa.
          </p>

          <!-- Local funnel warning -->
          <div
            v-if="typeof currentFunnel?.id === 'string' && currentFunnel?.id.startsWith('default-')"
            class="mb-4 px-3 py-2 rounded-lg bg-n-amber-3 text-n-amber-11 text-sm"
          >
            <span class="i-lucide-alert-triangle size-4 inline-block mr-1.5 align-text-bottom" />
            Funis locais não suportam mensagens de follow-up. Crie um funil no servidor para usar esta funcionalidade.
          </div>

          <!-- Stages list -->
          <div
            v-else
            class="space-y-2"
          >
            <div
              v-for="stage in orderedStages"
              :key="stage.name"
              class="flex items-center justify-between p-3 rounded-lg border border-n-weak hover:bg-n-alpha-1 transition-colors"
            >
              <div class="flex items-center gap-3">
                <div
                  class="w-1 h-8 rounded-full"
                  :style="{ backgroundColor: stage.color }"
                />
                <div>
                  <span class="text-sm font-medium text-n-slate-12">
                    {{ stage.label }}
                  </span>
                  <p class="text-xs text-n-slate-10 mt-0.5">
                    <span v-if="stage.totalCount > 0">
                      {{ stage.followUpCount }} de {{ stage.totalCount }} mensagem(ns) ativa(s)
                    </span>
                    <span v-else class="text-n-slate-9">
                      Nenhuma mensagem configurada
                    </span>
                  </p>
                </div>
              </div>
              <button
                class="px-3 py-1.5 text-xs font-medium text-n-slate-11 hover:text-n-slate-12 bg-n-alpha-2 hover:bg-n-alpha-3 rounded-lg transition-colors"
                @click="openFollowUpEditor(stage)"
              >
                <span class="i-lucide-settings-2 size-3 inline-block mr-1 align-text-bottom" />
                Configurar
              </button>
            </div>

            <div
              v-if="orderedStages.length === 0"
              class="text-center py-6 text-n-slate-10"
            >
              <span class="i-lucide-layers size-8 opacity-50" />
              <p class="text-sm mt-2">Nenhuma etapa configurada neste funil</p>
            </div>
          </div>
        </div>

        <!-- Botão Salvar -->
        <div class="flex justify-end pt-4">
          <Button icon="i-lucide-save" @click="saveSettings">
            Salvar Configurações
          </Button>
        </div>
      </div>
    </div>

    <!-- Empty State -->
    <div v-else class="flex-1 flex items-center justify-center p-6">
      <div class="text-center">
        <span class="i-lucide-kanban size-16 text-n-slate-10 opacity-50" />
        <h3 class="text-base font-medium text-n-slate-11 mt-4">
          Nenhum funil encontrado
        </h3>
        <p class="text-sm text-n-slate-10 mt-1">
          Crie um funil primeiro para configurar permissões
        </p>
        <Button
          variant="smooth"
          class="mt-4"
          @click="goBackToKanban"
        >
          Voltar ao Kanban
        </Button>
      </div>
    </div>

    <!-- Follow-up Message Editor Modal (outside v-if/v-else structure) -->
    <FollowUpMessageEditor
      v-if="selectedStageForFollowUp && currentFunnel"
      :is-open="showFollowUpEditor"
      :funnel-id="currentFunnel.id"
      :stage-name="selectedStageForFollowUp.name"
      :stage-label="selectedStageForFollowUp.label"
      :stage-color="selectedStageForFollowUp.color"
      @close="closeFollowUpEditor"
      @saved="handleFollowUpSaved"
    />
  </div>
</template>

<style scoped>
.funnel-select {
  background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e");
  background-position: right 0.5rem center;
  background-size: 1.25em 1.25em;
}
</style>
